import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import type { DescriptionFormat } from '@/types/galaxy-data/unit'
import { parse } from 'date-fns'

/**
 * Galaxy date is YYYY-MM-DD
 * When empty the value will be similar to
 * 0000-00-00 (return undefined in this case)
 * */
export function fieldDate(galaxyDate: string): Date | undefined {
  try {
    const date = parse(galaxyDate, 'yyyy-LL-dd', new Date())
    if (Number.isNaN(date.getTime())) {
      return undefined
    }
    return date
  } catch (error) {
    console.error(error)
    return undefined
  }
}

/**
 * Galaxy short description, from ID to word
 *
 * Type of short_description
 * - 0 yes
 * - 1 no
 * - 2 truncated
 */
export function fieldShortDescription(galaxyFormat: 0 | 1 | 2): DescriptionFormat {
  switch (galaxyFormat) {
    case 1:
      return 'none'
    case 2:
      return 'truncate'
    default:
      return 'full'
  }
}

/**
 * Galaxy date format convert, from an ID to a string value with date format
 *
 * BO Index = Value of "date_format_db2_id"
 * Requested = Follow the design
 * FrontSide Library = The front end uses a specific library (date-fns) which has its own date formatting system.
 * |----------|----------------------------|---------------------|---------------------|
 * | BO Index | Requested                  | Example Value       | FrontSide Library   |
 * |----------|----------------------------|---------------------|---------------------|
 * |    0     | Not Defined => Use Default | "12/31/2000"        | "MM/dd/yyyy"        |
 * |    1     | "mm/dd/yyyy" (DEFAULT)     | "12/31/2000"        | "MM/dd/yyyy"        |
 * |    2     | "dd/mm/yyyy"               | "31/12/2000"        | "dd/MM/yyyy"        |
 * |    3     | "DDD, MMM dd, yyyy"        | "Wed, Oct 31, 2000" | "EEE, MMM dd, yyyy" |
 * |    4     | "MMM dd, yyyy"             | "Oct 31, 2000"      | "MMM dd, yyyy"      |
 * |    5     | "dd MMM, yyyy"             | "31 Oct, 2000"      | "dd MMM, yyyy"      |
 * |    6     | "DDD, MMM dd"              | "Wed, Oct 31"       | "EEE, MMM dd"       |
 * |    7     | "MMM dd"                   | "Oct 31"            | "MMM dd"            |
 * |    8     | "dd MMM"                   | "31 Oct"            | "dd MMM"            |
 * |----------|----------------------------|---------------------|---------------------|
 *
 */
export function fieldDateFormat(
  galaxyDateFormat: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
):
  | 'MM/dd/yyyy'
  | 'dd/MM/yyyy'
  | 'EEE, MMM dd, yyyy'
  | 'MMM dd, yyyy'
  | 'dd MMM, yyyy'
  | 'EEE, MMM dd'
  | 'MMM dd'
  | 'dd MMM'
  | undefined {
  switch (galaxyDateFormat) {
    case 1:
      return 'MM/dd/yyyy' // (default)
    case 2:
      return 'dd/MM/yyyy'
    case 3:
      return 'EEE, MMM dd, yyyy'
    case 4:
      return 'MMM dd, yyyy'
    case 5:
      return 'dd MMM, yyyy'
    case 6:
      return 'EEE, MMM dd'
    case 7:
      return 'MMM dd'
    case 8:
      return 'dd MMM'
    // Invalid / No Defined / Zero:
    default:
      return 'MM/dd/yyyy'
  }
}

/**
 * Regex Date Format
 *
 * Return the corresponding regex for the provided dateFormat
 */
export function regexDateFormat(
  galaxyDateFormat:
    | 'MM/dd/yyyy'
    | 'dd/MM/yyyy'
    | 'EEE, MMM dd, yyyy'
    | 'MMM dd, yyyy'
    | 'dd MMM, yyyy'
    | 'EEE, MMM dd'
    | 'MMM dd'
    | 'dd MMM'
    | undefined,
): RegExp {
  switch (galaxyDateFormat) {
    case 'MM/dd/yyyy': // "12/31/2000" (DEFAULT)
      return /^(?<month>\d{2})\/(?<date>\d{2})\/(?<year>\d{4})$/
    case 'dd/MM/yyyy': // "12/31/2000"
      return /^(?<date>\d{2})\/(?<month>\d{2})\/(?<year>\d{4})$/
    case 'EEE, MMM dd, yyyy': // "Wed, Oct 31, 2000"
      return /^(?<daysText>\w{3,}), (?<month>\w{2,}) (?<days>\d{2}), (?<year>\d{4})$/
    case 'MMM dd, yyyy': // "Oct 31, 2000"
      return /^(?<month>\w{2,}) (?<days>\d{2}), (?<year>\d{4})$/
    case 'dd MMM, yyyy': // "31 Oct, 2000"
      return /^(?<days>\d{2}) (?<month>\w{2,}), (?<year>\d{4})$/
    case 'EEE, MMM dd': // "Wed, Oct 31"
      return /^(?<daysText>\w{3,}), (?<month>\w{2,}) (?<days>\d{2})$/
    case 'MMM dd': // "Oct 31"
      return /^(?<month>\w{2,}) (?<days>\d{2})$/
    case 'dd MMM': // "31 Oct"
      return /^(?<days>\d{2}) (?<month>\w{2,}) $/
    // Invalid / No Defined / Zero:
    default: // "12/31/2000" (DEFAULT)
      return /^(?<month>\d{2})\/(?<date>\d{2})\/(?<year>\d{4})$/
  }
}

/**
 * Sorting priority of meeting_room_amenities list by the "feature flag"
 *
 * Return "GalaxyMeetingRoom[]" with "is_featured" amenities put as priority in meeting_room_amenities
 */
export function sortMeetingRoomAmenitiesByFlagFeature(
  meetingRooms: GalaxyMeetingRoom[],
): GalaxyMeetingRoom[] {
  return meetingRooms.map((room) => {
    // Put "is_featured" in priority
    const sortedAmenities = room.meeting_room_amenities.filter((el) => el.is_featured)
    room.meeting_room_amenities = sortedAmenities.concat(
      room.meeting_room_amenities.filter((el) => !el.is_featured),
    )
    return room
  })
}
